import { useCallback, useMemo, useState } from "react";

/**
 * Display-only shopping cart for the Customer View.
 *
 * Returns: lines (item+qty pairs), count, total, plus mutator callbacks.
 * State shape is `{ [itemId]: qty }`.
 */
export function useCart(allItems) {
  const [cart, setCart] = useState({});

  const addItem = useCallback((it) => {
    setCart((c) => ({ ...c, [it.id]: (c[it.id] || 0) + 1 }));
  }, []);

  const decItem = useCallback((it) => {
    setCart((c) => {
      const n = (c[it.id] || 0) - 1;
      const next = { ...c };
      if (n <= 0) delete next[it.id];
      else next[it.id] = n;
      return next;
    });
  }, []);

  const clearCart = useCallback(() => setCart({}), []);

  const lines = useMemo(() => {
    return Object.entries(cart)
      .map(([id, qty]) => {
        const item = allItems.find((x) => x.id === id);
        return item ? { item, qty } : null;
      })
      .filter(Boolean);
  }, [cart, allItems]);

  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);
  const total = useMemo(
    () => lines.reduce((s, l) => s + l.qty * Number(l.item.price || 0), 0),
    [lines],
  );

  return { cart, lines, count, total, addItem, decItem, clearCart };
}
